/**
 * Helper used by stores to encrypt write payloads.
 *
 * Behavior:
 *   - No family / encryption not activated yet → return plaintext (bypass mode).
 *   - Encryption ACTIVE and key is in memory → return AES-GCM ciphertext.
 *   - Encryption ACTIVE but key NOT in memory yet (new device still syncing) →
 *     THROW. We refuse to write plaintext into an active-E2EE family because
 *     it would create un-decryptable records mixed in with the encrypted ones.
 */

import { aesEncrypt } from "./encryption";
import { getFamilyKeyInMemory } from "./sessionKeys";

export interface EncryptionActiveFamily {
  id: string;
  encryption_active_since?: number | null;
}

export class E2EENotReadyError extends Error {
  constructor() {
    super("E2EE is active for this family but this device hasn't synced the family key yet");
    this.name = "E2EENotReadyError";
  }
}

export const maybeEncryptForFamily = async (
  plaintext: string,
  family: EncryptionActiveFamily | null
): Promise<string> => {
  if (!plaintext) return plaintext;
  if (!family) return plaintext;
  if (!family.encryption_active_since) return plaintext; // bypass
  const key = getFamilyKeyInMemory(family.id);
  if (!key) throw new E2EENotReadyError();
  return await aesEncrypt(plaintext, key);
};

